import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import "./HelpQueryPopup.css";
import QueryIcon from "../../../images/query_icon.png";
import { queryService } from "../../../services/QueryService";
import { messageService } from "../../../services/MessageService";
import { driveService } from "../../../services/DriveService";
import { fetchEnum } from "../../../apis/enum";
import { toast } from "../../../utils/toast";
import { LoginContext } from "../../../context/LoginContext";
import { useContext } from "react";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { useNavigate } from "react-router-dom";
import { help_desk_query } from "../QueryChat/QueryChatPopup";

interface HelpQueryPopupProps {
  show?: boolean;
  onClose?: () => void;
  openMyQueries?: () => void;
}

interface FormData {
  name: string;
  category: string;
  form: string;
  description: string;
}

export default function HelpQueryPopup({
  // show,
  // onClose,
  // openMyQueries,
}: HelpQueryPopupProps) {
  const navigate = useNavigate();
  const show = true
  const onClose = () => navigate(-1)
  const openMyQueries = () => {
    navigate("/myQuery")
  }
  const { user } = useContext(LoginContext);
  const [formData, setFormData] = useState<FormData>({
    name: "",
    category: "",
    form: "",
    description: "",
  });
  const [drives, setDrives] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  const [dataToSave, setDataToSave] = useState<any>({});
  const [photoError, setPhotoError] = useState<string>("");

  const [quillMsg, setQuillMsg] = useState("")

  useEffect(() => {
    setFormData({ ...formData, ["description"]: quillMsg });
  }, [quillMsg])

  useEffect(() => {
    if (show) {
      fetchDrives();
      fetchCategories();
    }
  }, [show]);

  const fetchDrives = async () => {
    try {
      const driveData = await driveService.getData();
      setDrives(driveData);
    } catch (error) {
      console.error("Error fetching drives:", error);
    }
  };

  const fetchCategories = async () => {
    try {
      const data = await fetchEnum("QueryCategory");
      // data.resource is the enum values array
      // const values = data.resource || data.values || [];
      if (Array.isArray(data)) {
        setCategories(data);
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
    }
  };

  if (!show) return null;

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };


  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name || !formData.category || !formData.form || !formData.description) {
      toast.error("Please fill in all required fields.");
      return;
    }

    if (photoError) {
      toast.error("Please fix the photo size error before saving.");
      return;
    }

    setLoading(true);

    try {
      const userId = user?.userId || "";
      if (!userId) {
        toast.error("User not identified. Please log in again.");
        setLoading(false);
        return;
      }

      // Step 1: Create Query
      const queryObj: Record<string, string> = {
        subject: formData.name,
        category: formData.category,
        status: "Open",
        user_id: userId,
        drive_id: formData.form,
      };

      const jsonString = JSON.stringify(queryObj);
      const base64Encoded = btoa(unescape(encodeURIComponent(jsonString)));
      const queryParams = new FormData();
      queryParams.append("resource", base64Encoded);

      const queryResponse = await queryService.create({
        params: queryParams,
      });

      if (queryResponse.errCode === -1) {
        setLoading(false);
        return;
      }

      const queryId = queryResponse.resource[0]?.id;

      // Step 2: Create First Message
      const messageObj: Record<string, string> = {
        sendertype: "Student",
        message: formData.description,
        query_Id: queryId,
        user_id: userId,
      };

      const messageParams = new FormData();

      let selectedFile = null;
      selectedFile = Object.keys(dataToSave).filter((key) => dataToSave[key] instanceof File);
      if (selectedFile !== undefined && selectedFile.length > 0) {
        messageParams.append("photo", dataToSave[selectedFile[0]]);
        dataToSave[selectedFile[0]] = "";

        messageParams.append("description", "message attachment");
        messageParams.append("appId", "admission-iiitb");
        messageParams.append("dmsRole", "admin");
        messageParams.append("user_id", userId);
        messageParams.append("tags", "t1,t2,attend");
      }

      const msgJsonString = JSON.stringify(messageObj);
      const msgBase64Encoded = btoa(unescape(encodeURIComponent(msgJsonString)));
      messageParams.append("resource", msgBase64Encoded);

      const messageResponse = await messageService.create({
        params: messageParams,
      });

      if (messageResponse.errCode === -1) {
        setLoading(false);
        return;
      }

      toast.success("Query submitted successfully!");

      // Reset form
      setFormData({ name: "", category: "", form: "", description: "" });
      openMyQueries();
      onClose();
    } catch (error) {
      console.error("Error submitting query:", error);
      toast.error("Failed to submit query. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="help-popup-overlay" onClick={onClose}>
      <div className="help-popup-content" onClick={(e) => e.stopPropagation()}>
        <div className="help-popup-header">
          <div className="query-header-tabs">
            <h2>Ask your Query</h2>
            <div className="closeBox">
              <button className="myquery-btn" onClick={openMyQueries}>
                <img src={QueryIcon} className="query_icon" alt="Query Icon" />{" "}
                My Query
              </button>
              <button className="help-popup-close" onClick={onClose}>
                <X size={20} />
              </button>
            </div>
          </div>
        </div>

        <form className="help-query-form" onSubmit={handleSubmit}>
          <div className="help-form-row">
            <div className="help-field">
              <label>Query Subject*</label>
              <input
                type="text"
                name="name"
                placeholder="Enter query subject"
                value={formData.name}
                onChange={handleChange}
              />
            </div>

            <div className="help-field">
              <label>Query Category*</label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
              >
                <option value="">Select Query Category</option>
                {categories.map((cat: any) => (
                  <option key={cat} value={cat}>
                    {cat}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="help-field">
            <label>Select Program*</label>
            <select name="form" value={formData.form} onChange={handleChange}>
              <option value="">Select Program</option>
              {drives.map((drive: any) => (
                <option key={drive.id} value={drive.id}>
                  {drive.name || drive.drive_name || drive.id}
                </option>
              ))}
            </select>
          </div>

          <div className="help-field">
            <label>Query's Description / Message*</label>
            <ReactQuill
              theme="snow"
              id="description"
              value={quillMsg}
              placeholder="Ask your query"
              onChange={setQuillMsg} />
          </div>

          <div className="help-form-row">
            <div className="help-field">
              <label>Upload Attachment</label>
              <input
                className="form-input file-input"
                type="file"
                name="photo"
                accept="image/jpeg,image/png,image/jpg, application/pdf"
                onChange={(e) => {
                  const file = e.target.files?.[0] || null;
                  if (file && file.size > 1024 * 1024) {
                    setPhotoError("Photo size must be less than 1024 KB. Please choose a smaller file.");
                    e.target.value = "";
                    setDataToSave({ ...dataToSave, photo: null });
                    return;
                  } else {
                    setPhotoError("")
                    setDataToSave({
                      ...dataToSave,
                      photo: e.target.files?.[0] || null,
                    })
                  }
                }
                }
              />
              {photoError && <span style={{ color: "red", fontSize: "0.8rem", marginTop: "4px", display: "block" }}>{photoError}</span>}
              <small style={{ color: "#6b7280", fontSize: "12px", marginTop: "4px", display: "block" }}>
                Max size: 1024 KB | Formats: JPG, JPEG, PNG, PDF
              </small>
            </div>
          </div>

          <div className="help-submit">
            <button type="submit" disabled={loading}>
              {loading ? "Submitting..." : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
